import { Locale, endOfYear, format, isAfter, isBefore, parseISO, startOfDay, startOfToday, subDays } from "date-fns";
import { TFunction } from "i18next";
import * as Yup from "yup";

import { SeizableIncomePeriodResponse } from "@/api";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";

import { SeizableIncomeCalculatorFormValues } from "./SeizableIncomeCalculatorDialog.types";

export const MAX_DEPENDENTS = 5;

export const EXECUTION_TYPE_STANDARD = "standard";
export const EXECUTION_TYPE_MAINTENANCE = "maintenance";

export const TRANSLATION_BASE = "calculators.seizableIncome";

const PeriodIdFormat = "yyyy-MM-dd";

function formatPeriodDate(date: Date, locale: Locale | undefined): string {
  return format(date, "P", { locale });
}

export function buildPeriodOptions(
  periods: SeizableIncomePeriodResponse[],
  t: TFunction,
  locale: Locale | undefined
): IOptionItem[] {
  return periods.map((period) => {
    const id = toPeriodId(period);
    const from = formatPeriodDate(period.from, locale);

    // A period ends the day before the next one starts; the last one is shown up to the end of its year.
    const toDate = period.to == null ? endOfYear(period.from) : subDays(period.to, 1);
    const to = formatPeriodDate(toDate, locale);

    return { id, label: t(`${TRANSLATION_BASE}.fields.periodOption`, { from, to }) };
  });
}

export function toPeriodId(period: SeizableIncomePeriodResponse): string {
  return format(period.from, PeriodIdFormat);
}

export function fromPeriodId(periodId: string): Date {
  return parseISO(periodId);
}

/** The period today falls in; periods that start later can still be picked by hand. */
export function findCurrentPeriod(periods: SeizableIncomePeriodResponse[]): SeizableIncomePeriodResponse | undefined {
  const today = startOfToday();
  return periods.find(
    (period) =>
      !isAfter(startOfDay(period.from), today) && (period.to == null || isBefore(today, startOfDay(period.to)))
  );
}

export function buildExecutionTypeOptions(t: TFunction): IOptionItem[] {
  return [
    { id: EXECUTION_TYPE_STANDARD, label: t(`${TRANSLATION_BASE}.executionTypes.standard`) },
    { id: EXECUTION_TYPE_MAINTENANCE, label: t(`${TRANSLATION_BASE}.executionTypes.maintenance`) },
  ];
}

export function createSeizableIncomeValidationSchema(t: TFunction) {
  return Yup.object().shape({
    monthlyIncome: Yup.number()
      .required(t("common.validation.fieldRequired", { fieldName: t(`${TRANSLATION_BASE}.fields.monthlyIncome`) }))
      .min(0, t(`${TRANSLATION_BASE}.validation.monthlyIncomeNegative`)),
    period: Yup.string()
      .nullable()
      .required(t("common.validation.fieldRequired", { fieldName: t(`${TRANSLATION_BASE}.fields.period`) })),
    executionType: Yup.string()
      .nullable()
      .required(t("common.validation.fieldRequired", { fieldName: t(`${TRANSLATION_BASE}.fields.executionType`) })),
    maintenanceObligationCount: Yup.number()
      .nullable()
      .max(MAX_DEPENDENTS, ({ max }) => t(`${TRANSLATION_BASE}.validation.maintenanceCountMax`, { max })),
    maintenanceSeizureCount: Yup.number()
      .nullable()
      .max(MAX_DEPENDENTS, ({ max }) => t(`${TRANSLATION_BASE}.validation.maintenanceCountMax`, { max }))
      .test(
        "seizure-not-exceed-obligations",
        t(`${TRANSLATION_BASE}.validation.seizureCountExceedsObligations`),
        function seizureWithinObligations(value) {
          const { executionType, maintenanceObligationCount } = this.parent as SeizableIncomeCalculatorFormValues;
          // The seizure count only applies to an Unterhaltsexekution; in standard mode the field is
          // hidden and ignored, so a leftover value must not block the calculation.
          if (executionType !== EXECUTION_TYPE_MAINTENANCE) {
            return true;
          }
          return (value ?? 0) <= (maintenanceObligationCount ?? 0);
        }
      ),
  });
}
